import React, { Fragment, PureComponent, SyntheticEvent } from 'react';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import FormControl from '@mui/material/FormControl';
import ReactTooltip from 'react-tooltip';
import _ from 'lodash';
import moment from 'moment';
import { AssetKeys } from '../../../api/vault/searchEngine/interfaces';
import {
  selectClassPrefix,
  SelectItem,
  TextFieldFilter,
  DatePicker,
  SmallTextFieldFilter,
  StyledFormControl,
} from './styles';
import {
  IProps, IDispatchProps, IStateProps,
} from './interfaces';
import {
  updateFilterValues,
  updateSelectedFilterOptions,
} from '../../../state/filterAssets';
import { IAppState } from '../../../state/reducers';
import { Product } from '../../../state/product';
import { queryGamesAction, resetPage } from '../../../state/search';
import { ISearchResultsLocaleLookupKeys } from '../../../localization/interfaces';
import { LocaleKeyTree } from '../../../localization';
import { KeyboardKey } from '../../../enums';
import { SelectOptionType } from '../../../utils/selection';
import {
  ResultsHeaderCell,
  ResultsTableHeaderRow,
  ResultsCheckboxCell,
  ResultHeaderThumbnailCell,
  ResultHeaderTitleCell,
  ResultHeaderTitleExpandedCell,
} from '../SearchTable/styles';
import { gtmVaultEvent } from '../../../gtm';
import { parseMidnightDatefromString } from '../../../utils/time';
import { selectTagsByOrgId } from '../../../state/tags';

class FilterPanel extends PureComponent<IProps> {
  private readonly localeLookupKeys: Readonly<ISearchResultsLocaleLookupKeys> = LocaleKeyTree.searchResults;
  private readonly dropDownColumns = [AssetKeys.AssetType, AssetKeys.AwayTeam, AssetKeys.HomeTeam, AssetKeys.Sport, AssetKeys.VideoType];

  onSelectFilterBlur = (event: SyntheticEvent<HTMLInputElement>) => {
    const columnName: string = (event.target as HTMLInputElement).id;
    const { selectedFilterOptions } = this.props;
    const selectedOptions: Array<string> = selectedFilterOptions[columnName]
      ? selectedFilterOptions[columnName].map((option: SelectOptionType<string>) => option.value) : [];
    this.handleFilterChange(selectedOptions, columnName);
  };

  handleFilterChange = (value: string | Array<string>, columnName: string) => {
    if (value && value.length) {
      this.updateFilterState(value, columnName);
    } else {
      this.resetFilterState(columnName);
    }
  };

  handleDateChange = (value: Date, columnName: string) => {
    const dateValid = (value) ? new Date(value) : null;

    const start = moment(dateValid).startOf('day').utc(true).toISOString();
    const end = moment(dateValid).endOf('day').utc(true).toISOString();

    // this is currently off by 1ing +1 day forwards
    if (start && end) {
      this.updateFilterState([start, end], columnName);
    } else {
      this.resetFilterState(columnName);
    }
  };

  handleKeyPressChange = (event: any, columnName: string) => {
    if ((this.dropDownColumns.indexOf(columnName) !== -1) || (event.key === KeyboardKey.Enter)) {
      const {
        queryGamesActionConnect,
        resetPageConnect,
      } = this.props;
      resetPageConnect();
      queryGamesActionConnect();
    }
  };

  updateFilterState = async (value: string | Array<string> | Date, columnName: string) => {
    const {
      filters,
      updateFilterValuesConnect,
      queryGamesActionConnect,
    } = this.props;

    updateFilterValuesConnect({
      ...filters,
      [columnName]: value,
    });
    if (typeof value !== 'string') {
      // if text field, do not apply filters until button pressed or input cleared
      queryGamesActionConnect();
    }

    gtmVaultEvent({
      action: 'filterChange',
      value: columnName,
    });
  };

  resetFilterState = async (columnName: string) => {
    const {
      filters,
      updateFilterValuesConnect,
      queryGamesActionConnect,
    } = this.props;
    const { [columnName]: columnValue, ...rest } = filters;

    updateFilterValuesConnect(rest);
    queryGamesActionConnect();
  };

  constructMenuOptions = (columnName: string): Array<SelectOptionType<string>> => {
    const { searchResponse, securityProfileOptions, orgTags } = this.props;
    const options: Array<any> = [];

    if (!searchResponse) {
      return options;
    }

    switch (columnName) {
      case AssetKeys.AssetType:
        options.push(...searchResponse.aggregations.type_aggs.buckets);
        break;
      case AssetKeys.Sport:
        options.push(...searchResponse.aggregations.sport_aggs.buckets);
        break;
      case AssetKeys.HomeTeam:
        options.push(...searchResponse.aggregations.homeTeam_aggs.buckets);
        break;
      case AssetKeys.AwayTeam:
        options.push(...searchResponse.aggregations.awayTeam_aggs.buckets);
        break;
      case AssetKeys.VideoType:
        options.push(...searchResponse.aggregations.videoType_aggs.buckets);
        break;
      case AssetKeys.Qualifier:
        options.push(...searchResponse.aggregations.exchangeQualifier_aggs.buckets);
        break;
      case AssetKeys.Owner:
        options.push(...searchResponse.aggregations.owner_aggs.buckets);
        return _.sortBy(options.filter((o) => o.key)
          .map((o) => {
            const option = securityProfileOptions.find(({ value }) => value === o.key);
            return option || {
              label: o.key,
              value: o.key,
            };
          }), 'label');
      case AssetKeys.OrgTagIds:
        options.push(...searchResponse.aggregations.orgTagIds_aggs.buckets);
        break;
      default:
        return options;
    }

    // Hide not owned orgTagIds and apply correct name of tags
    if (columnName === AssetKeys.OrgTagIds) {
      const menuOptions: SelectOptionType<string>[] = [];
      options.forEach((o) => {
        if (o.key) {
          const foundTag = orgTags.find((tag) => tag._id === o.key);
          if (foundTag) {
            menuOptions.push({
              label: foundTag.name,
              value: o.key,
            });
          } else if (o.key === 'N/A') {
            menuOptions.push({
              label: o.key,
              value: o.key,
            });
          }
        }
      });
      menuOptions.sort((a, b) => a.label.localeCompare(b.label));
      return menuOptions;
    }
    return options
      .filter((o) => o.key)
      .map((o) => ({
        label: o.key,
        value: o.key,
      }));
  };

  renderFilterForColumn = (columnName: string) => {
    const {
      filters,
      selectedFilterOptions,
      searchResponse,
      updateSelectedFilterOptionsConnect,
      isOwnedFilter,
    } = this.props;
    const initialRender = searchResponse === null;

    const value = filters[columnName] ? filters[columnName] : '';
    const id = (columnName === AssetKeys.AssetType) ? `Filter.${AssetKeys.ThumbnailImageUrl}` : `Filters.${columnName}`;
    if (columnName === AssetKeys.Title) {
      return (
        <TextFieldFilter
          id={id}
          disabled={initialRender}
          margin="normal"
          variant="outlined"
          value={value}
          inputProps={{ 'aria-label': 'bare', style: { height: 0.2, fontSize: '12px', padding: '14px' } }}
          onChange={(e: any) => this.handleFilterChange(e.target.value, columnName)}
          onKeyPress={(e: any) => this.handleKeyPressChange(e, columnName)}
        />
      );
    }
    if (columnName === AssetKeys.GameNumber) {
      return (
        <SmallTextFieldFilter
          id={id}
          disabled={initialRender}
          margin="normal"
          variant="outlined"
          value={value}
          inputProps={{ 'aria-label': 'bare', style: { height: 0.2, fontSize: '12px', padding: '14px' } }}
          onChange={(e: any) => this.handleFilterChange(e.target.value, columnName)}
          onKeyPress={(e: any) => this.handleKeyPressChange(e, columnName)}
        />
      );
    }

    if (columnName === AssetKeys.GameDate || columnName === AssetKeys.CreatedDate) {
      const val = parseMidnightDatefromString((value[1]) as string || null);

      return (
        <DatePicker
          // id={id}
          disabled={initialRender}
          format="MM-dd-yyyy"
          value={val}
          // KeyboardButtonProps={DatePickerButtonProps}
          // InputProps={DatePickerInputStyles}
          onChange={(e) => this.handleDateChange(e as Date | null, columnName)}
          // onKeyPress={(e: any) => this.handleKeyPressChange(e, columnName)}
          slotProps={{
            actionBar: {
              actions: ['clear', 'today'],
            },
          }}
        />
      );
    }

    const val = (value) || [];
    const options = this.constructMenuOptions(columnName);
    const selectedOptions = selectedFilterOptions[columnName];

    const select = (
      <SelectItem
        id={id}
        inputId={columnName}
        classNamePrefix={selectClassPrefix}
        options={options}
        placeholder=""
        value={selectedOptions || null}
        onChange={(opts: unknown) => updateSelectedFilterOptionsConnect(columnName, opts as Array<SelectOptionType<string>>)}
        onBlur={this.onSelectFilterBlur}
        isMulti
        isDisabled={initialRender || !options || (isOwnedFilter && columnName === AssetKeys.Owner)}
        closeMenuOnSelect={false}
      />
    );

    if (value && value.length) {
      let dataTip = '';
      if ((columnName === AssetKeys.Owner || columnName === AssetKeys.OrgTagIds) && selectedOptions) {
        dataTip = selectedOptions.map((o) => o.label).toString();
      } else {
        dataTip = val.toString();
      }
      return (
        <>
          <StyledFormControl
            variant="outlined"
            disabled={initialRender}
            data-tip={dataTip}
            data-for="filter-tooltip"
          >
            {select}
          </StyledFormControl>
          <ReactTooltip
            id="filter-tooltip"
            delayShow={250}
            effect="solid"
          />
        </>
      );
    }
    return (
      <StyledFormControl variant="outlined" disabled={initialRender}>
        {select}
      </StyledFormControl>
    );
  };

  render() {
    const {
      withOwner,
      isExchange,
      isMyVault,
      isVault,
    } = this.props;
    return (
      <ResultsTableHeaderRow key="filterRow" id="filterRow">
        <ResultsCheckboxCell align="left" padding="checkbox" />
        <ResultHeaderThumbnailCell align="left">{this.renderFilterForColumn(AssetKeys.AssetType)}</ResultHeaderThumbnailCell>
        {isVault ? (
          <ResultHeaderTitleExpandedCell align="left" style={{ display: 'flex', gap: '16px' }}>
            {this.renderFilterForColumn(AssetKeys.Title)}
            {this.renderFilterForColumn(AssetKeys.OrgTagIds)}
          </ResultHeaderTitleExpandedCell>
        ) : (
          <ResultHeaderTitleCell align="left">{this.renderFilterForColumn(AssetKeys.Title)}</ResultHeaderTitleCell>
        )}
        <ResultsHeaderCell align="left">{this.renderFilterForColumn(AssetKeys.GameDate)}</ResultsHeaderCell>
        <ResultsHeaderCell align="left">{this.renderFilterForColumn(AssetKeys.Sport)}</ResultsHeaderCell>
        <ResultsHeaderCell align="left">{this.renderFilterForColumn(AssetKeys.AwayTeam)}</ResultsHeaderCell>
        <ResultsHeaderCell align="left">{this.renderFilterForColumn(AssetKeys.HomeTeam)}</ResultsHeaderCell>
        {(!isExchange && !isMyVault) && (
          <ResultsHeaderCell align="left">{this.renderFilterForColumn(AssetKeys.VideoType)}</ResultsHeaderCell>
        )}
        {isExchange && (
          <>
            <ResultsHeaderCell align="left">{this.renderFilterForColumn(AssetKeys.Qualifier)}</ResultsHeaderCell>
            <ResultsHeaderCell align="left">{this.renderFilterForColumn(AssetKeys.GameNumber)}</ResultsHeaderCell>
          </>
        )}
        {withOwner && (
          <ResultsHeaderCell align="left">{this.renderFilterForColumn(AssetKeys.Owner)}</ResultsHeaderCell>
        )}
        <ResultsHeaderCell align="left">{this.renderFilterForColumn(AssetKeys.CreatedDate)}</ResultsHeaderCell>
      </ResultsTableHeaderRow>
    );
  }
}

const mapStateToProps = (state: IAppState): IStateProps => ({
  searchResponse: state.search?.searchResponse,
  filters: state.filterAssets?.filters,
  selectedFilterOptions: state.filterAssets?.selectedFilterOptions ?? {},
  securityProfileOptions: state.securityProfiles?.options ?? [],
  isOwnedFilter: state.search?.isOwnedFilter ?? false,
  isExchange: state.product?.target === Product.Exchange,
  isMyVault: state.product?.target === Product.MyVault,
  isVault: state.product?.target === Product.Vault,
  orgTags: selectTagsByOrgId(state),
});

const mapDispatchToProps: IDispatchProps = {
  updateFilterValuesConnect: updateFilterValues,
  updateSelectedFilterOptionsConnect: updateSelectedFilterOptions,
  queryGamesActionConnect: queryGamesAction,
  resetPageConnect: resetPage,
};

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(FilterPanel));
